import { API_URL } from "../constants";
import {
  getAccessToken,
  setAccessToken,
  removeAccessToken,
} from "../utils/storage";

interface AuthProvider {
  login: (credentials: { username: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  checkError: (error: { status: number }) => Promise<void>;
  getIdentity: () => Promise<{ id: string; fullName: string }>;
  getPermissions: () => Promise<string>;
}

const authProvider: AuthProvider = {
  async login({ username, password }) {
    try {
      const response = await fetch(`${API_URL}/auth`, {
        method: "POST",
        body: JSON.stringify({ email: username, password }),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Login failed");
      }

      const { accessToken } = await response.json();
      if (!accessToken) {
        throw new Error("No access token received");
      }
      setAccessToken(accessToken);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Network error during login");
    }
  },

  async logout() {
    removeAccessToken();
  },

  async checkAuth() {
    if (!getAccessToken()) {
      throw new Error("Not authenticated");
    }
  },

  async checkError(error) {
    const { status } = error;
    if (status === 401 || status === 403) {
      removeAccessToken();
      throw new Error("Not authenticated");
    }
  },

  async getIdentity() {
    try {
      const token = getAccessToken();
      if (!token) {
        throw new Error("Not authenticated");
      }

      // Intentar decodificar el JWT para obtener información del usuario
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return {
          id: payload.sub || "user",
          fullName: payload.fullName || "Admin",
        };
      } catch {
        // Si no se puede decodificar, intentar obtener desde la API
        const response = await fetch(`${API_URL}/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const user = await response.json();
          return {
            id: user.id || "user",
            fullName: user.fullName || "Admin",
          };
        }
      }

      // Fallback a valores por defecto
      return {
        id: "user",
        fullName: "Admin",
      };
    } catch {
      return {
        id: "user",
        fullName: "Admin",
      };
    }
  },

  async getPermissions() {
    return "";
  },
};

export default authProvider;
